import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Event } from '../types';

export function useEvents(limit?: number) {
  const { ready } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('events')
        .select(`
          *,
          host:profiles!host_user_id (
            id, full_name, address, phone, spouse_name, avatar_url
          )
        `)
        .order('date', { ascending: true });

      if (limit) query = query.limit(limit);

      const { data, error } = await query;
      if (error) setError(error.message);
      else setEvents((data ?? []) as Event[]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    if (!ready) return;
    fetchEvents();
  }, [ready, fetchEvents]);

  return { events, loading, error, refetch: fetchEvents };
}

export function useUpcomingEvent() {
  const { ready } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUpcoming = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          host:profiles!host_user_id (
            id, full_name, address, phone, spouse_name, avatar_url
          )
        `)
        .eq('type', 'sabbath_lunch')
        .gte('date', today)
        .order('date', { ascending: true })
        .limit(1);

      if (!error && data && data.length > 0) {
        setEvent(data[0] as Event);
      } else {
        setEvent(null);
      }
    } catch {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchUpcoming();
  }, [ready, fetchUpcoming]);

  return { event, loading, refetch: fetchUpcoming };
}

export function useEvent(eventId: string | undefined) {
  const { ready } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchEvent = useCallback(async () => {
    if (!eventId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          host:profiles!host_user_id (
            id, full_name, address, phone, spouse_name, avatar_url
          )
        `)
        .eq('id', eventId)
        .single();

      if (!error && data) setEvent(data as Event);
    } catch {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!ready) return;
    fetchEvent();
  }, [ready, fetchEvent]);

  return { event, loading, refetch: fetchEvent };
}
